import { ProfileBadgesQuery } from 'graphql/__generated__/ProfileBadgesQuery.graphql';
import { PROFILE_BADGES_QUERY } from 'graphql/ProfileBadgesQuery';
import { useLazyLoadQuery } from 'react-relay';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import {
  BadgesEdge,
  BadgesEdges,
  UnifiedProfileForBadges,
} from 'constants/types';
import MainPanelSectionHeader from 'components/common/section/MainPanelSectionHeader';
import ElevatedContainer from 'components/common/section/ElevatedContainer';
import useIsPersonalView from 'utils/useIsPersonalView';
import SectionSummaryContainer from 'components/common/section/SectionSummaryContainer';

import MostRecentBadges from './MostRecentBadges';
import MostRecentLearningAchievements from './MostRecentLearningAchievements';

export type Props = {
  internalId: string;
  profileHasConsented: boolean;
};

const BadgesSummary = ({ internalId, profileHasConsented }: Props) => {
  const isPersonalView = useIsPersonalView(internalId);

  const badgesResponse = useLazyLoadQuery<ProfileBadgesQuery>(
    PROFILE_BADGES_QUERY,
    { internalId },
  ).unified?.unifiedProfile as UnifiedProfileForBadges;

  const forumsBadgesEdges =
    (badgesResponse?.badges?.edges as BadgesEdges) || [];
  const recentLearningAchievementBadgesEdges =
    (badgesResponse?.learning?.recentLearningAchievementBadges
      ?.edges as BadgesEdges) || [];

  const forumsBadges = forumsBadgesEdges.map((edge: BadgesEdge) => ({
    ...(edge?.node ?? {}),
    src: edge?.node?.imageUrl,
  }));

  const recentLearningAchievementBadges =
    recentLearningAchievementBadgesEdges.map((edge: BadgesEdge) => ({
      ...(edge?.node ?? {}),
      src: edge?.node?.imageUrl,
    }));

  const totalForumsBadges = badgesResponse?.badges?.totalCount ?? 0;
  const totalLearningBadges =
    badgesResponse?.learning?.recentLearningAchievementBadges?.totalCount ?? 0;
  const totalNumberOfBadges = totalForumsBadges + totalLearningBadges;
  const titleParts = [totalNumberOfBadges || ''];
  titleParts.push(totalNumberOfBadges === 1 ? 'Badge' : 'Badges');

  // ideally this scenario should never happen since one badge always exist, but this behavior has been seen due to bunchball connection issue
  if (!isPersonalView && !totalNumberOfBadges) {
    return null;
  }

  return (
    <SectionSummaryContainer>
      <MainPanelSectionHeader
        testId="badges-summary-title"
        title={titleParts.join(' ')}
        link={`/profile/${internalId}/badges`}
        linkText={totalNumberOfBadges ? 'View all' : undefined}
        subtitle={
          isPersonalView ? (
            <span>
              {'Earn badges for your activity and learning achievements.'}
            </span>
          ) : undefined
        }
        viewAllAnalyticsName="viewAllBadges"
      />
      <ElevatedContainer>
        <div css={avatarContainerStyles}>
          <MostRecentLearningAchievements
            isPersonalView={isPersonalView}
            recentLearningAchievementBadges={recentLearningAchievementBadges}
            profileHasConsented={profileHasConsented}
          />
          <MostRecentBadges
            isPersonalView={isPersonalView}
            recentBadges={forumsBadges}
          />
        </div>
      </ElevatedContainer>
    </SectionSummaryContainer>
  );
};
export default BadgesSummary;

const avatarContainerStyles = css({
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
  gap: token('space.600'),
  '@media (max-width: 767px)': {
    flexDirection: 'column',
    gap: token('space.300'),
  },
});
